/**
 * Password Change Modal
 *
 * Blocking modal shown when a user must change their password.
 * This modal cannot be dismissed - the user must complete the password change.
 */

import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import {
  BlockingMessage,
  ErrorBanner,
  MinLengthIndicator,
  MismatchMessage,
  ModalHeader,
  PasswordField,
  SignOutLink,
  SubmitButton,
} from './password-change-modal.parts'
import { usePasswordChangeForm } from './use-password-change-form'
import { useAuth } from '../hooks/use-auth'

interface PasswordChangeModalProps {
  onPasswordChanged: () => void
}

export function PasswordChangeModal({ onPasswordChanged }: PasswordChangeModalProps): ReactElement {
  const { t } = useTranslation('auth')
  const form = usePasswordChangeForm(onPasswordChanged)
  const { logout } = useAuth()

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm" />
      <div className="relative w-full max-w-md overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/20 dark:border-slate-800 dark:bg-slate-900 dark:shadow-black/40">
        <ModalHeader />
        <form
          onSubmit={(e) => {
            void form.handleSubmit(e)
          }}
          className="space-y-5 p-6"
        >
          <ErrorBanner error={form.error} />
          <PasswordField
            id="current-password"
            label={t('passwordChange.currentPasswordLabel')}
            placeholder={t('passwordChange.currentPasswordPlaceholder')}
            autoComplete="current-password"
            value={form.currentPassword}
            onChange={form.setCurrentPassword}
            showPassword={form.showCurrentPassword}
            onToggleShow={form.toggleShowCurrentPassword}
          />
          <div>
            <PasswordField
              id="new-password"
              label={t('passwordChange.newPasswordLabel')}
              placeholder={t('passwordChange.newPasswordPlaceholder')}
              autoComplete="new-password"
              value={form.newPassword}
              onChange={form.setNewPassword}
              showPassword={form.showNewPassword}
              onToggleShow={form.toggleShowNewPassword}
            />
            <MinLengthIndicator valid={form.isNewPasswordValid} />
          </div>
          <div>
            <PasswordField
              id="confirm-password"
              label={t('passwordChange.confirmPasswordLabel')}
              placeholder={t('passwordChange.confirmPasswordPlaceholder')}
              autoComplete="new-password"
              value={form.confirmPassword}
              onChange={form.setConfirmPassword}
              showPassword={form.showConfirmPassword}
              onToggleShow={form.toggleShowConfirmPassword}
              hasError={form.hasMismatch}
            />
            {form.hasMismatch && <MismatchMessage />}
          </div>
          <SubmitButton
            disabled={!form.isFormValid || form.isSubmitting}
            submitting={form.isSubmitting}
          />
          <BlockingMessage />
          <SignOutLink
            onSignOut={() => {
              void logout()
            }}
          />
        </form>
      </div>
    </div>
  )
}
